import React from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from './BrandLogo';

const popularColors = [
  { name: 'Sage Green', hex: '9CAF88' },
  { name: 'Coral', hex: 'FF7F50' },
  { name: 'Gold', hex: 'FFD700' },
  { name: 'Indigo', hex: '4B0082' },
  { name: 'Crimson', hex: 'DC143C' },
  { name: 'Forest Green', hex: '228B22' },
  { name: 'Teal', hex: '008080' },
  { name: 'Orange', hex: 'FFA500' },
  { name: 'Royal Blue', hex: '4169E1' },
  { name: 'Hot Pink', hex: 'FF69B4' },
];

const linkClass = 'rounded-sm hover:text-brand-700 transition-colors';

const Footer: React.FC = () => (
  <footer className="py-10 sm:py-12">
    <div className="grid grid-cols-1 md:grid-cols-[1fr_1.25fr_1fr] gap-8">
      {/* Brand */}
      <div>
        <BrandLogo />
        <p className="text-sm text-ink-secondary mt-4 leading-relaxed max-w-xs">
          Free color explorer for designers and developers. Check contrast, generate harmonies, and export design tokens.
        </p>
      </div>

      {/* Tools & Learning */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <h3 className="text-xs font-semibold text-ink uppercase tracking-wider mb-2">Tools</h3>
          <nav className="flex flex-col gap-2 text-sm text-ink-secondary">
            <Link to="/colors" className={linkClass}>Named Colors</Link>
            <Link to="/color-palettes" className={linkClass}>Color Palettes</Link>
            <Link to="/compare" className={linkClass}>Compare Colors</Link>
            <Link to="/image-color-picker" className={linkClass}>Image Color Picker</Link>
            <Link to="/converter" className={linkClass}>Color Converter</Link>
            <Link to="/contrast-checker" className={linkClass}>Contrast Checker</Link>
            <Link to="/link-contrast-checker" className={linkClass}>Link Contrast Checker</Link>
            <Link to="/studio" className={linkClass}>Palette Studio</Link>
            <Link to="/brand-builder" className={linkClass}>Brand Builder</Link>
          </nav>
        </div>
        <div>
          <h3 className="text-xs font-semibold text-ink uppercase tracking-wider mb-2">Learn</h3>
          <nav className="flex flex-col gap-2 text-sm text-ink-secondary">
            <Link to="/theory" className={linkClass}>Color Theory</Link>
            <Link to="/models" className={linkClass}>Color Models</Link>
            <Link to="/methodology" className={linkClass}>Calculation Methods</Link>
            <Link to="/validation" className={linkClass}>Validation Data</Link>
            <Link to="/editorial-policy" className={linkClass}>Editorial Policy</Link>
            <Link to="/blog" className={linkClass}>Blog</Link>
          </nav>
        </div>
      </div>

      {/* Popular Colors */}
      <div>
        <h3 className="text-xs font-semibold text-ink uppercase tracking-wider mb-2">Popular Colors</h3>
        <div className="flex flex-wrap gap-2">
          {popularColors.map((c) => (
            <Link
              key={c.hex}
              to={`/color/${c.hex}`}
              className="group flex items-center gap-1.5 text-sm text-ink-secondary hover:text-ink-secondary transition-colors"
            >
              <span
                className="w-3 h-3 rounded-sm border border-surface-border shrink-0"
                style={{ backgroundColor: `#${c.hex}` }}
              />
              <span className="group-hover:underline">{c.name}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>

    <div className="mt-6 pt-4 border-t border-surface-border flex flex-wrap gap-x-5 gap-y-2 text-xs text-ink-muted">
      <Link to="/about" className={linkClass}>About</Link>
      <Link to="/contact" className={linkClass}>Contact</Link>
      <Link to="/privacy" className={linkClass}>Privacy Policy</Link>
      <Link to="/terms" className={linkClass}>Terms of Service</Link>
      <button onClick={() => window.dispatchEvent(new Event("color-match:privacy"))} className={linkClass}>Privacy settings</button>
      <Link to="/sitemap" className={linkClass}>Sitemap</Link>
    </div>

    <div className="mt-4 text-xs text-ink-muted text-center">
      &copy; {new Date().getFullYear()} Color Match. All rights reserved. &middot;{' '}
      <Link to="/contact" className={linkClass}>Contact information</Link>
    </div>
  </footer>
);

export default Footer;
